import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar } from "recharts";
import { LayoutGrid, BookOpen, Compass, Users, CalendarDays, LogOut, Trash2, MessageCircle, Package } from "lucide-react";
import { PATHWAYS } from "../data/content";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "courses", label: "My Courses", icon: BookOpen },
  { id: "assessment", label: "Assessment", icon: Compass },
  { id: "community", label: "Community", icon: Users },
  { id: "orders", label: "Orders", icon: Package },
  { id: "sessions", label: "Sessions", icon: CalendarDays },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab || "overview");
  const [enrollments, setEnrollments] = useState([]);
  const [results, setResults] = useState([]);
  const [posts, setPosts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);

  const load = async () => {
    const [e, r, p, b, o] = await Promise.all([
      api.get("/enrollments"), api.get("/assessment/results"), api.get("/community/posts"), api.get("/bookings"), api.get("/orders"),
    ]);
    setEnrollments(e.data); setResults(r.data); setPosts(p.data); setBookings(b.data); setOrders(o.data);
  };
  useEffect(() => { load(); }, []);

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen flex bg-[#FAF4E9]">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-[#1E3A2B] text-[#FAF4E9] p-8 fixed h-full">
        <Link to="/" className="font-serif-d text-xl">Life With <span className="italic-emph text-[#DCBB6C]">Nata</span></Link>
        <p className="text-[#FAF4E9]/50 text-xs mt-1 uppercase tracking-widest">Member Hub</p>
        <nav className="mt-12 flex flex-col gap-1 flex-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${tab === t.id ? "bg-[#2E5240] text-[#DCBB6C]" : "text-[#FAF4E9]/70 hover:text-[#FAF4E9]"}`}>
                <Icon size={18} /> {t.label}
              </button>
            );
          })}
        </nav>
        {user?.role === "admin" && (
          <Link
            to="/admin/orders"
            data-testid="admin-orders-shortcut"
            className="flex items-center gap-3 px-4 py-3 text-sm text-[#DCBB6C]/80 hover:text-[#DCBB6C] border-t border-[#FAF4E9]/10 mt-2"
          >
            <Package size={18} /> Admin · Orders
          </Link>
        )}
        <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-3 px-4 py-3 text-sm text-[#FAF4E9]/60 hover:text-[#E7AEB4]">
          <LogOut size={18} /> Log Out
        </button>
      </aside>

      {/* Mobile tab bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#1E3A2B] flex justify-around py-3">
        {TABS.map((t) => { const Icon = t.icon; return (
          <button key={t.id} onClick={() => setTab(t.id)} className={tab === t.id ? "text-[#DCBB6C]" : "text-[#FAF4E9]/60"}><Icon size={20} /></button>
        );})}
      </div>

      {/* Main */}
      <main className="flex-1 md:ml-64 px-6 md:px-12 py-12 pb-24">
        <div className="max-w-4xl">
          <p className="eyebrow">Welcome back</p>
          <h1 className="font-serif-d text-4xl md:text-5xl text-[#1E3A2B] tracking-tight mt-2">{user?.name?.split(" ")[0]}.</h1>

          {tab === "overview" && <Overview enrollments={enrollments} results={results} bookings={bookings} orders={orders} setTab={setTab} />}
          {tab === "courses" && <Courses enrollments={enrollments} />}
          {tab === "assessment" && <AssessmentTab results={results} />}
          {tab === "community" && <Community posts={posts} reload={load} />}
          {tab === "orders" && <Orders orders={orders} />}
          {tab === "sessions" && <Sessions bookings={bookings} reload={load} />}
        </div>
      </main>
    </div>
  );
}

const Stat = ({ v, l }) => (
  <div className="bg-white border border-[#21302A]/10 p-6">
    <p className="font-serif-d text-4xl text-[#1E3A2B]">{v}</p>
    <p className="text-xs uppercase tracking-widest text-[#21302A]/60 mt-1">{l}</p>
  </div>
);

function Overview({ enrollments, results, bookings, orders, setTab }) {
  return (
    <div className="mt-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat v={enrollments.length} l="Programs" />
        <Stat v={results.length} l="Assessments" />
        <Stat v={bookings.length} l="Sessions" />
        <Stat v={orders.length} l="HN Orders" />
      </div>
      <div className="mt-10 bg-[#1E3A2B] text-[#FAF4E9] p-8">
        <p className="eyebrow text-[#DCBB6C]">Your Next Step</p>
        <p className="font-serif-d text-2xl mt-3 leading-snug">
          {results.length === 0 ? "Start with your LifePrints™ map." : "Pick a pathway for this season."}
        </p>
        <div className="flex gap-3 mt-6 flex-wrap">
          <Link to="/assessment" className="bg-[#C0953A] text-[#132619] hover:bg-[#DCBB6C] transition-colors px-6 py-3 uppercase tracking-widest text-xs font-semibold">
            {results.length === 0 ? "Take Assessment →" : "Retake Assessment →"}
          </Link>
          <button onClick={() => setTab("courses")} className="border border-[#FAF4E9]/30 hover:bg-[#FAF4E9]/10 transition-colors px-6 py-3 uppercase tracking-widest text-xs">View Courses</button>
        </div>
      </div>
    </div>
  );
}

function Courses({ enrollments }) {
  return (
    <div className="mt-10">
      <h2 className="font-serif-d text-2xl text-[#1E3A2B]">My Courses & Progress</h2>
      {enrollments.length === 0 ? (
        <div className="mt-6 border border-dashed border-[#21302A]/20 p-10 text-center">
          <p className="text-[#21302A]/60">You haven&apos;t enrolled in a program yet.</p>
          <Link to="/transform" className="inline-block mt-6 bg-[#1E3A2B] text-[#FAF4E9] px-6 py-3 uppercase tracking-widest text-xs">Explore Pathways →</Link>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {enrollments.map((e) => (
            <div key={e.enrollment_id} className="bg-white border border-[#21302A]/10 p-6" data-testid={`enrollment-${e.program_id}`}>
              <div className="flex justify-between items-center">
                <h3 className="font-serif-d text-xl text-[#1E3A2B]">{e.program_name}</h3>
                <span className="text-xs uppercase tracking-widest text-[#C0953A]">{e.progress}% complete</span>
              </div>
              <div className="h-1 bg-[#21302A]/10 mt-4"><div className="h-full bg-[#C0953A]" style={{ width: `${e.progress}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AssessmentTab({ results }) {
  const latest = results[0];
  if (!latest) return (
    <div className="mt-10 border border-dashed border-[#21302A]/20 p-10 text-center">
      <p className="text-[#21302A]/60">No assessment results yet.</p>
      <Link to="/assessment" className="inline-block mt-6 bg-[#1E3A2B] text-[#FAF4E9] px-6 py-3 uppercase tracking-widest text-xs">Take the LifePrints™ Assessment →</Link>
    </div>
  );
  const chartData = latest.domains.map((d) => ({ domain: d.label.split(" ")[0], score: d.score }));
  return (
    <div className="mt-10" data-testid="assessment-results">
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <p className="eyebrow">Overall Wholeness</p>
          <p className="font-serif-d text-7xl text-[#C0953A]">{latest.overall}%</p>
          <div className="mt-6 space-y-2">
            <p className="text-xs uppercase tracking-widest text-[#21302A]/50">Strongest</p>
            {latest.highest.map((h) => <p key={h.key} className="text-sm text-[#1E3A2B]">{h.label}</p>)}
            <p className="text-xs uppercase tracking-widest text-[#21302A]/50 mt-4">Growth Areas</p>
            {latest.lowest.map((h) => <p key={h.key} className="text-sm text-[#C77E88]">{h.label}</p>)}
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData}>
              <PolarGrid stroke="#21302A20" />
              <PolarAngleAxis dataKey="domain" tick={{ fontSize: 10, fill: "#21302A" }} />
              <Radar dataKey="score" stroke="#C0953A" fill="#C0953A" fillOpacity={0.4} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="mt-10 bg-[#1E3A2B] text-[#FAF4E9] p-8" data-testid="ai-report">
        <p className="eyebrow text-[#DCBB6C]">Your AI Insight Report · from Coach Nata</p>
        <div className="mt-4 whitespace-pre-line leading-relaxed text-[#FAF4E9]/90 font-sans-d">{latest.report}</div>
      </div>
    </div>
  );
}

function PostCard({ p, user, reload }) {
  const [showComments, setShowComments] = useState(false);
  const [comment, setComment] = useState("");
  const isAdmin = user?.role === "admin";
  const canDelete = isAdmin || p.user_id === user?.user_id;
  const liked = (p.liked_by || []).includes(user?.user_id);
  const comments = p.comments || [];

  const like = async () => { await api.post(`/community/posts/${p.post_id}/like`); reload(); };
  const remove = async () => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/community/posts/${p.post_id}`); reload(); toast.success("Post removed."); }
    catch { toast.error("Could not delete post."); }
  };
  const removeComment = async (cid) => {
    try { await api.delete(`/community/posts/${p.post_id}/comments/${cid}`); reload(); }
    catch { toast.error("Could not delete comment."); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try { await api.post(`/community/posts/${p.post_id}/comments`, { content: comment }); setComment(""); reload(); }
    catch { toast.error("Could not comment."); }
  };

  return (
    <div className="bg-white border border-[#21302A]/10 p-5" data-testid="community-post">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#E7AEB4] flex items-center justify-center font-serif-d text-[#1E3A2B]">{p.author?.[0]?.toUpperCase()}</div>
        <div className="flex items-center gap-2 flex-1">
          <p className="text-sm font-semibold text-[#1E3A2B]">{p.author}</p>
          {p.author_role === "admin" && (
            <span className="bg-[#C0953A] text-[#132619] text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 font-bold" data-testid="coach-badge">Coach</span>
          )}
        </div>
        {canDelete && (
          <button onClick={remove} data-testid="delete-post-btn" aria-label="Delete post"
            className="text-[#21302A]/40 hover:text-[#C77E88] transition-colors"><Trash2 size={15} /></button>
        )}
      </div>
      <p className="mt-3 text-[#21302A]/80 text-sm">{p.content}</p>
      <div className="mt-3 flex items-center gap-5">
        <button onClick={like} data-testid="like-btn"
          className={`text-xs uppercase tracking-widest transition-colors ${liked ? "text-[#C77E88]" : "text-[#C0953A] hover:text-[#C77E88]"}`}>
          {liked ? "♥" : "♡"} {p.likes || 0}
        </button>
        <button onClick={() => setShowComments(!showComments)} data-testid="comments-toggle"
          className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#21302A]/50 hover:text-[#1E3A2B] transition-colors">
          <MessageCircle size={13} /> {comments.length}
        </button>
      </div>
      {showComments && (
        <div className="mt-4 pt-4 border-t border-[#21302A]/10 space-y-3">
          {comments.map((c) => (
            <div key={c.comment_id} className="flex items-start gap-2.5" data-testid="post-comment">
              <div className="w-7 h-7 shrink-0 rounded-full bg-[#DCBB6C]/40 flex items-center justify-center font-serif-d text-xs text-[#1E3A2B]">{c.author?.[0]?.toUpperCase()}</div>
              <div className="flex-1 bg-[#FAF4E9] px-3 py-2">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-[#1E3A2B]">{c.author}</p>
                  {c.author_role === "admin" && <span className="bg-[#C0953A] text-[#132619] text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 font-bold">Coach</span>}
                </div>
                <p className="text-sm text-[#21302A]/80 mt-0.5">{c.content}</p>
              </div>
              {(isAdmin || c.user_id === user?.user_id) && (
                <button onClick={() => removeComment(c.comment_id)} data-testid="delete-comment-btn" aria-label="Delete comment"
                  className="text-[#21302A]/30 hover:text-[#C77E88] mt-1.5"><Trash2 size={13} /></button>
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <input data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Add an encouragement…"
              className="flex-1 bg-[#FAF4E9] px-3 py-2 text-sm outline-none text-[#21302A]" />
            <button data-testid="comment-submit" onClick={addComment}
              className="bg-[#1E3A2B] text-[#FAF4E9] px-4 py-2 uppercase tracking-widest text-[10px]">Reply</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Community({ posts, reload }) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const submit = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try { await api.post("/community/posts", { content }); setContent(""); reload(); toast.success("Posted to the community!"); }
    catch { toast.error("Could not post."); } finally { setPosting(false); }
  };
  return (
    <div className="mt-10" data-testid="community-feed">
      <div className="flex items-center justify-between">
        <h2 className="font-serif-d text-2xl text-[#1E3A2B]">The Community Feed</h2>
        {user?.role === "admin" && <span className="bg-[#1E3A2B] text-[#DCBB6C] text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 font-semibold" data-testid="admin-mode-badge">Moderator Mode</span>}
      </div>
      <div className="mt-6 bg-white border border-[#21302A]/10 p-5">
        <textarea data-testid="post-input" value={content} onChange={(e) => setContent(e.target.value)} rows={3} placeholder="Share a win, a prayer, a breakthrough…"
          className="w-full bg-transparent outline-none text-[#21302A] resize-none" />
        <div className="flex justify-end">
          <button data-testid="post-submit" onClick={submit} disabled={posting} className="bg-[#1E3A2B] text-[#FAF4E9] px-5 py-2.5 uppercase tracking-widest text-xs disabled:opacity-50">Share →</button>
        </div>
      </div>
      <div className="mt-6 space-y-4">
        {posts.length === 0 && <p className="text-[#21302A]/50 text-sm">Be the first to share with the community.</p>}
        {posts.map((p) => <PostCard key={p.post_id} p={p} user={user} reload={reload} />)}
      </div>
    </div>
  );
}

function Orders({ orders }) {
  if (!orders || orders.length === 0) return (
    <div className="mt-10" data-testid="orders-tab">
      <h2 className="font-serif-d text-2xl text-[#1E3A2B]">Haus of Nata Orders</h2>
      <div className="mt-6 border border-dashed border-[#21302A]/20 p-10 text-center">
        <p className="text-[#21302A]/60">No orders yet. Your closet is calling.</p>
        <Link to="/store" className="inline-block mt-6 bg-[#0B0B0B] text-[#DCBB6C] px-6 py-3 uppercase tracking-widest text-xs">Shop the Drop →</Link>
      </div>
    </div>
  );
  const fmt = (d) => { try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return d; } };
  return (
    <div className="mt-10" data-testid="orders-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif-d text-2xl text-[#1E3A2B]">Haus of Nata Orders</h2>
        <Link to="/store" className="text-xs uppercase tracking-widest text-[#C0953A] hover:text-[#1E3A2B]">Shop again →</Link>
      </div>
      <div className="mt-6 space-y-3">
        {orders.map((o) => (
          <div key={o.order_id} className="bg-[#0B0B0B] text-[#FAF4E9] p-5 border border-[#C0953A]/25" data-testid={`order-${o.order_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#C0953A]">Order · {fmt(o.created_at)}</p>
                <p className="font-serif-d text-xl mt-1">{o.product_name}</p>
                <p className="text-xs text-[#FAF4E9]/60 mt-1">Size {o.size} · Qty {o.quantity}</p>
              </div>
              <div className="text-right">
                <p className="font-serif-d text-2xl text-[#DCBB6C]">${(o.amount || 0).toFixed(2)}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#8FA88B] mt-1">{o.status}</p>
              </div>
            </div>
            {o.shipping_address && (
              <div className="mt-4 pt-4 border-t border-[#C0953A]/15 text-xs text-[#FAF4E9]/60">
                <span className="uppercase tracking-widest text-[#C0953A] text-[10px]">Ships to · </span>
                {o.shipping_address.name}, {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}
              </div>
            )}
            {o.tracking_number && (
              <div className="mt-2 text-xs text-[#DCBB6C]" data-testid={`order-tracking-${o.order_id}`}>
                <span className="uppercase tracking-widest text-[10px]">Tracking · </span>
                {o.carrier?.toUpperCase()} · {o.tracking_number}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Sessions({ bookings, reload }) {
  const [form, setForm] = useState({ pathway: PATHWAYS[0].title, preferred_date: "", note: "" });
  const [loading, setLoading] = useState(false);
  const book = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/bookings", form); reload(); toast.success("Discovery call requested!"); setForm({ ...form, preferred_date: "", note: "" }); }
    catch { toast.error("Could not book."); } finally { setLoading(false); }
  };
  const field = "bg-transparent border-b border-[#21302A]/20 focus:border-[#1E3A2B] outline-none px-0 py-2.5 w-full text-[#21302A]";
  return (
    <div className="mt-10" data-testid="sessions-tab">
      <h2 className="font-serif-d text-2xl text-[#1E3A2B]">Book a Discovery Call</h2>
      <form onSubmit={book} className="mt-6 bg-white border border-[#21302A]/10 p-6 space-y-5 max-w-lg">
        <select data-testid="booking-pathway" className={field} value={form.pathway} onChange={(e) => setForm({ ...form, pathway: e.target.value })}>
          {PATHWAYS.map((p) => <option key={p.id} value={p.title}>{p.title}</option>)}
        </select>
        <input data-testid="booking-date" type="date" required className={field} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} />
        <textarea data-testid="booking-note" rows={2} placeholder="What are you carrying right now?" className={field} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        <button data-testid="booking-submit" disabled={loading} className="bg-[#C0953A] text-[#132619] hover:bg-[#DCBB6C] transition-colors px-6 py-3 uppercase tracking-widest text-xs font-semibold disabled:opacity-50">Request Call →</button>
      </form>
      <div className="mt-8 space-y-3">
        {bookings.map((b) => (
          <div key={b.booking_id} className="bg-white border border-[#21302A]/10 p-5 flex justify-between items-center" data-testid="booking-item">
            <div>
              <p className="font-serif-d text-lg text-[#1E3A2B]">{b.pathway}</p>
              <p className="text-xs text-[#21302A]/60">{b.preferred_date}</p>
            </div>
            <span className="text-xs uppercase tracking-widest text-[#C0953A]">{b.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
